import { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ReactFlowProvider, useEdgesState, useNodesState } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import '../../index.css';
import { createPortal } from 'react-dom';
import { usePageHeader } from '../../../user';
import WorkflowDiagram from '../../components/workflow-diagram';
import NodeDetailsPanel from '../../components/node-detail-panel';
import { initialEdges, initialNodes } from '../../data/constants';
import {
  ApiIcon,
  AssistantIcon,
  GatewayIcon,
  PromptIcon,
  SchedulerIcon,
  WebhookIcon,
} from '../../../../../assets/images';
import {
  WorkFlowActionContext,
  WorkFlowContext,
  WorkflowSelectModalContext,
  DisableWorkflowContext,
  initialWorkFlowContext,
} from '../../../../../utils/context';
import { createNewWorkflow, getActionInputFields, getTriggersInputFields } from '../../services';
import makeToast from '../../../../../components/toaster';
import Logout from '../../../../../auth/protected/session-expire-modal';
import { verifyAccess } from '../../../../../services';
import ActionModal from '../../../../../components/action-modal';

function WorkFlowAutoMator() {
  const navigate = useNavigate();
  const { workflowSelectModalOpen, setWorkflowSelectModalOpen } = useContext(
    WorkflowSelectModalContext,
  );
  const { workFlowData, setWorkFlowData } = useContext(WorkFlowContext);
  const { workFlowActionData, setWorkFlowActionData } = useContext(WorkFlowActionContext);
  const { disableMode, setDisableMode } = useContext(DisableWorkflowContext);
  const [nodes, setNodes] = useNodesState(initialNodes);
  const [edges, setEdges] = useEdgesState(initialEdges);
  const [selectedNode, setSelectedNode] = useState<{
    data: {
      unique_id: number;
      trigger_type: string;
      selected?: boolean;
      node_type?: string;
      description?: string;
    };
    id: string;
  } | null>(null);
  const [activeTab, setActiveTab] = useState(0);
  const [modalData, setModalData] = useState<
    | {
        id: number;
        name: string;
        description: string;
        actions: { id?: string; name?: string; description?: string }[];
        app: string;
      }[]
    | null
  >(null);
  const [openPanel, setOpenPanel] = useState(false);
  const { projectId, workflowId } = useParams();
  const [logout, setLogout] = useState<boolean>(false);

  useEffect(() => {
    const wfId =
      workflowId || JSON.parse(localStorage.getItem('workFlowData') || '{}')?.trigger?.workflow_id;
    // Get the value of the 'viewDetails' parameter
    const viewDetails = localStorage.getItem('disableMode');
    const hasAccess = verifyAccess(
      'automator',
      `/automator/workflow/${projectId}${wfId ? `/${wfId}` : ''}`,
    );
    setDisableMode(!!viewDetails);
    if (!hasAccess) {
      navigate('/unauthorized');
    }
  }, []);

  useEffect(() => {
    if (projectId && !workflowId && workFlowData.new_project) {
      createNewWorkflow({ project_id: projectId })
        .then((res) => {
          if (res?.status === 201 || res?.status === 200) {
            const data = res?.data?.data;
            setWorkFlowData({
              workflow_id: data?.id,
              new_project: false,
            });
            setWorkFlowActionData({
              workflow_id: data?.id,
            });
            setWorkflowSelectModalOpen(false);
          } else if (res?.status === 401) {
            setLogout(true);
          }
        })
        .catch((err) => {
          const error = err?.response?.data?.message;
          makeToast(error, 'Error creating workflow');
        });
    }
  }, []);

  useEffect(() => {
    if (selectedNode?.id === '1' && selectedNode?.data?.selected === true) {
      getTriggersInputFields()
        .then((response) => {
          if (response?.status === 200) {
            const data = response?.data?.data;
            setModalData(data);
            setActiveTab(0);
          } else if (response?.status === 401) {
            setLogout(true);
          }
        })
        .catch((error) => {
          throw error;
        });
      setWorkflowSelectModalOpen(true);
    } else if (selectedNode?.id !== '1' && selectedNode?.data?.selected === true) {
      getActionInputFields()
        .then((response) => {
          if (response?.status === 200) {
            const data = response?.data?.data;
            setModalData(data);
            setActiveTab(0);
          } else if (response?.status === 401) {
            setLogout(true);
          }
        })
        .catch((error) => {
          throw error;
        });
      setWorkflowSelectModalOpen(true);
    }
  }, [selectedNode]);

  const handleCloseModal = () => {
    setSelectedNode((prev) => {
      if (!prev) return null;
      return { ...prev, data: { ...prev.data, selected: false } };
    });
    setWorkflowSelectModalOpen(false);
  };

  const handleTriggerSelection = (triggerType: {
    id: number;
    name: string;
    description: string;
    actions: { id?: string; name?: string; description?: string }[];
    app: string;
  }) => {
    setSelectedNode((prev) => ({
      ...prev,
      data: {
        ...prev?.data,
        unique_id: prev?.data?.unique_id ?? 0,
        trigger_type: triggerType?.app,
        description: 'Select the Event',
        selected: false,
        node_type: prev?.data?.node_type ?? '',
      },
      id: String(prev?.id) ?? '0',
    }));
    setWorkFlowActionData({
      ...workFlowActionData,
      action_catalogue_id: triggerType?.actions?.[0]?.id,
      action_config: {},
      isSetUpTabCompleted: false,
      isConfigTabCompleted: false,
      isTestTabCompleted: false,
    });
    setWorkFlowData({
      ...initialWorkFlowContext.workFlowData,
      workflow_id: workFlowData.workflow_id,
    });
    setWorkflowSelectModalOpen(false);
    const savedValue = JSON.parse(localStorage.getItem('workFlowData')!);
    if (selectedNode?.data.node_type === 'trigger') {
      localStorage.setItem(
        'workFlowData',
        JSON.stringify({
          ...(savedValue?.actions?.length > 0
            ? { actions: [...(savedValue?.actions ?? [])] }
            : null),
          trigger: {
            trigger_id: savedValue?.trigger?.trigger_id,
          },
        }),
      );
    }
  };

  const appIcons: Record<string, string> = {
    Schedule: SchedulerIcon,
    'Webhook API': WebhookIcon,
    API: ApiIcon,
    Gateways: GatewayIcon,
    Assistants: AssistantIcon,
    'Send Prompt': PromptIcon,
  };

  const getAppIcon = (app: string) => appIcons[app] || SchedulerIcon;

  usePageHeader({ className: 'relative' });

  return (
    <>
      <ReactFlowProvider>
        <div
          className="workflow-container relative overflow-y-scroll
          thin-scroller"
          style={{ height: '78vh', width: '100%' }}
        >
          {/* REACT FLOW  */}
          <WorkflowDiagram
            setSelectedNode={setSelectedNode}
            setNodes={setNodes}
            nodes={nodes}
            edges={edges}
            setEdges={setEdges}
            selectedNode={selectedNode}
            setOpenPanel={setOpenPanel}
            setActiveTab={setActiveTab}
            setLogout={setLogout}
          />

          {/* SIDE PANEL */}
          {openPanel && (
            <NodeDetailsPanel
              selectedNode={selectedNode}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
              // nodes={nodes}
              setNodes={setNodes}
              modalData={modalData}
              setOpenPanel={setOpenPanel}
              disableMode={disableMode}
              setLogout={setLogout}
              // setSelectedNode={setSelectedNode}
            />
          )}
        </div>
        <ActionModal
          className="w-[400px]"
          modalLogo=""
          modalTitle="Select Your Trigger"
          open={workflowSelectModalOpen && !disableMode}
          isActionsNeeded={false}
          resetData={handleCloseModal}
        >
          <div className="space-y-3">
            {modalData?.map((item) => (
              <button
                type="button"
                key={item.app}
                className="flex border w-full p-2 rounded-lg cursor-pointer"
                onClick={() => handleTriggerSelection(item)}
              >
                <img src={getAppIcon(item.app)} alt={`${item.app} icon`} className="w-[14%]" />
                <div className="ml-2">
                  <p className="font-extrabold text-lg text-left">{item.app}</p>
                  <p className="text-orange-500 font-light text-sm">
                    {item.description || 'Click here to apply'}
                  </p>
                </div>
              </button>
            ))}
          </div>
        </ActionModal>
      </ReactFlowProvider>
      {logout && createPortal(<Logout />, document.body)}
    </>
  );
}
export default WorkFlowAutoMator;
